"use client";

import { Capacitor, registerPlugin } from "@capacitor/core";
import { useEffect, useRef } from "react";
import { createClientLogger } from "@/lib/logging/client";
import {
  type ActivityCommandHandlers,
  type CommandScope,
  dispatchActivityCommand,
  type Subscriber,
} from "@/lib/native/activity-command-dispatcher";
import { isUnimplementedNativeCall } from "@/lib/native/bridge-availability";
import type { OwnedWidgetStats, WidgetStats } from "@/lib/native/widget-stats";

export type { ActivityCommandHandlers } from "@/lib/native/activity-command-dispatcher";

import {
  isNativeWorkoutEnded,
  markNativeWorkoutEnded,
} from "@/lib/native/workout-identity";
import { useAuthState } from "./use-auth-state";

const logger = createClientLogger("hooks/use-live-activity-commands");

export type HiiveActivityKind = "movement" | "workout";
export type HiiveActivityAction =
  /** Watch-initiated only. The native engine has already begun tracking by the
   * time this reaches JS — see HiiveMovementActivityPlugin — so the handler's
   * job is to catch the web layer up, not to start a second session. */
  | "start"
  | "pause"
  | "resume"
  | "end"
  | "completeSet"
  | "skipRest"
  | "addRest"
  | "subtractRest";

export interface HiiveActivityCommand {
  activity: HiiveActivityKind;
  id?: string;
  accountId?: string;
  sessionId?: string;
  setId?: string;
  action: HiiveActivityAction;
  value?: number;
  /** "run" | "walk" | "hike" | "ride", on a watch-initiated `start`. */
  activityType?: string;
  at: number;
}

export interface HiiveWatchActivityState {
  accountId?: string;
  activity: HiiveActivityKind | "";
  state: string;
  title?: string;
  message?: string;
  startedAtMs?: number;
  endedAtMs?: number;
  disposition?: "completed" | "discarded";
  sessionId?: string;
  currentSetDurationSeconds?: number;
  currentSetDistanceMeters?: number;
  currentSetTrackMode?: "reps" | "time" | "distance";
  distanceMeters?: number;
  elapsedSeconds?: number;
  paceSecondsPerKm?: number;
  speedMps?: number;
  exerciseCount?: number;
  setCount?: number;
  totalSetCount?: number;
  currentExercise?: string;
  currentSetId?: string;
  currentSetNumber?: number;
  currentSetWeightKg?: number;
  /** Presentation preference only; all transported measurements remain kg. */
  preferredWeightUnit?: "kg" | "lb";
  currentSetReps?: number;
  /** The workout, kept separate from `title` — which becomes "Rest" while
   * resting. The watch header shows the workout for the whole session. */
  workoutName?: string;
  /** Completed weight × reps, in kg. The watch shows it as "Volume". */
  totalVolumeKg?: number;
  /** Needed alongside `restEndMs` so the watch can draw a countdown ring: the
   * end alone gives no denominator. */
  restStartMs?: number;
  restEndMs?: number;
}

type HiiveLiveActivityBridgePlugin = {
  setAccount(input: {
    accountId: string | null;
  }): Promise<{ changed: boolean }>;
  acknowledgeAccountCleanup(input: { accountId: string | null }): Promise<void>;
  startListening(): Promise<void>;
  peekCommands(): Promise<{ commands: HiiveActivityCommand[] }>;
  acknowledgeCommands(input: { ids: string[] }): Promise<void>;
  drainCommands(): Promise<{ commands: HiiveActivityCommand[] }>;
  writeStats(input: { accountId: string; stats: WidgetStats }): Promise<void>;
  reloadWidgets(): Promise<void>;
  publishActivity(input: {
    state: HiiveWatchActivityState & { updatedAtMs: number };
  }): Promise<{ supported: boolean }>;
  addListener(
    eventName: "liveActivityCommandsPending",
    listener: () => void,
  ): Promise<{ remove: () => Promise<void> }>;
};

export const HiiveLiveActivityBridge =
  registerPlugin<HiiveLiveActivityBridgePlugin>("HiiveLiveActivityBridge");

/** Window event dispatched by the deep-link handler for `?action=` taps
 * (iOS 16 fallback + notification taps) so they route through the same
 * handlers as native App Intent / broadcast commands. */
export const ACTIVITY_ACTION_EVENT = "hiive:activity-action";

const isNative = (): boolean => {
  if (typeof window === "undefined") return false;
  return Capacitor.isNativePlatform();
};

const subscribers = new Set<{ current: Subscriber }>();
let processing: Promise<void> = Promise.resolve();
let pendingDrain: Promise<void> | null = null;
const appliedInMemory = new Set<string>();
const dispatchCommand = (command: HiiveActivityCommand) =>
  dispatchActivityCommand(command, subscribers, appliedInMemory);

/**
 * `peekCommands`/`acknowledgeCommands` only exist in binaries built from
 * 2026-09-08. Older installs still carry the destructive `drainCommands`, and
 * without this fallback their Lock Screen / watch Pause and End buttons were
 * never processed at all — the peek failed and the command sat in the queue.
 */
let peekUnavailable = false;

async function readPendingCommands(): Promise<{
  commands: HiiveActivityCommand[];
  retained: boolean;
}> {
  if (!peekUnavailable) {
    try {
      const { commands } = await HiiveLiveActivityBridge.peekCommands();
      return { commands, retained: true };
    } catch (error) {
      if (!isUnimplementedNativeCall(error)) throw error;
      peekUnavailable = true;
    }
  }
  const { commands } = await HiiveLiveActivityBridge.drainCommands();
  return { commands, retained: false };
}

async function drainCommands() {
  if (pendingDrain) return pendingDrain;
  pendingDrain = (async () => {
    // Reading is non-destructive. A failed handler or an unmounted destination
    // retains its command in native storage for a later foreground pass.
    const { commands, retained } = await readPendingCommands();
    for (const command of commands) {
      processing = processing
        .catch(() => {})
        .then(async () => {
          if ((await dispatchCommand(command)) && retained && command.id) {
            await HiiveLiveActivityBridge.acknowledgeCommands({
              ids: [command.id],
            });
          }
        });
      await processing;
    }
  })()
    .catch((error) => {
      logger.warn("[Commands] pending command retained", error);
    })
    .finally(() => {
      pendingDrain = null;
    });
  return pendingDrain;
}

export function useLiveActivityCommands(
  handlers: ActivityCommandHandlers,
  scope: CommandScope = {},
) {
  const accountId = useAuthState().identity?.id;
  const subscriber = useRef({ handlers, scope });
  subscriber.current = { handlers, scope: { ...scope, accountId } };
  useEffect(() => {
    if (isNative() && accountId && scope.sessionId) void drainCommands();
  }, [accountId, scope.sessionId]);
  useEffect(() => {
    subscribers.add(subscriber);
    let cancelled = false;
    const listeners: { remove: () => Promise<void> }[] = [];
    const onWindowAction = (event: Event) => {
      const command = (event as CustomEvent<HiiveActivityCommand>).detail;
      if (!command) return;
      processing = processing
        .catch(() => {})
        .then(async () => {
          await dispatchCommand(command);
        });
      void processing.catch((error) =>
        logger.warn("[Commands] action failed", error),
      );
    };
    window.addEventListener(ACTIVITY_ACTION_EVENT, onWindowAction);
    if (isNative())
      void (async () => {
        try {
          await HiiveLiveActivityBridge.startListening();
          const pending = await HiiveLiveActivityBridge.addListener(
            "liveActivityCommandsPending",
            () => {
              void drainCommands();
            },
          );
          if (cancelled) {
            await pending.remove();
            return;
          }
          listeners.push(pending);
          const { App } = await import("@capacitor/app");
          const resume = await App.addListener("resume", () => {
            void drainCommands();
          });
          if (cancelled) {
            await resume.remove();
            return;
          }
          listeners.push(resume);
          await drainCommands();
        } catch (error) {
          logger.warn(
            "[Commands] native controls require the updated app",
            error,
          );
        }
      })();
    return () => {
      cancelled = true;
      subscribers.delete(subscriber);
      window.removeEventListener(ACTIVITY_ACTION_EVENT, onWindowAction);
      for (const listener of listeners) void listener.remove();
    };
  }, []);
}

/** Publish stats for the home / lock-screen widgets, then refresh them. */
export async function publishWidgetStats(snapshot: OwnedWidgetStats) {
  if (!isNative()) return;
  try {
    await HiiveLiveActivityBridge.writeStats({
      accountId: snapshot.accountId,
      stats: { ...snapshot.stats, updatedAtMs: Date.now() },
    });
  } catch (error) {
    logger.warn("[Widget] writeStats failed", error);
  }
}

/** Send the current workout or movement snapshot to the watchOS companion. */
export async function publishWatchActivity(
  state: HiiveWatchActivityState,
): Promise<void> {
  if (
    !isNative() ||
    typeof window === "undefined" ||
    Capacitor.getPlatform() !== "ios"
  ) {
    return;
  }

  if (state.activity === "workout" && state.sessionId) {
    if (state.state === "ended") markNativeWorkoutEnded(state.sessionId);
    else if (isNativeWorkoutEnded(state.sessionId)) return;
  }
  try {
    await HiiveLiveActivityBridge.publishActivity({
      state: { ...state, updatedAtMs: Date.now() },
    });
  } catch (error) {
    logger.warn("[Watch] activity sync failed", error);
    if (state.disposition) throw error;
  }
}
